import { useRef, useMemo, useCallback } from "react";

export type SelectableItem<T> = {
  id: string;
  label: string;
  value: string;
  original: T;
};

export type UseSelectableSearchProps<T> = {
  items: T[];
  getItemId: (item: T) => string;
  getItemLabel: (item: T) => string;
  onAdd?: (item: T) => void;
  onRemove?: (item: T) => void;
  maxItems?: number;
};

type SearchHandleRef = {
  value: string;
  setValue: (value: string, reset?: boolean) => void;
};

export type UseSelectableSearchReturn<T> = {
  selectedItems: SelectableItem<T>[];
  handleAdd: (item: T) => void;
  handleRemove: (item: SelectableItem<T>) => void;
  searchRef: React.RefObject<SearchHandleRef>;
  canAddMore: boolean;
};

export const useSelectableSearch = <T>({
  items,
  getItemId,
  getItemLabel,
  onAdd,
  onRemove,
  maxItems
}: UseSelectableSearchProps<T>): UseSelectableSearchReturn<T> => {
  const searchRef = useRef<SearchHandleRef>(null);

  const selectedItems = useMemo(() => {
    return items.map(item => ({
      id: getItemId(item),
      label: getItemLabel(item),
      value: getItemId(item),
      original: item
    }));
  }, [items, getItemId, getItemLabel]);

  const canAddMore = maxItems === undefined || items.length < maxItems;

  const handleAdd = useCallback(
    (item: T) => {
      if (!canAddMore) return;

      onAdd?.(item);

      if (searchRef.current?.setValue) {
        searchRef.current.setValue("", true);
      }
    },
    [onAdd, canAddMore]
  );

  const handleRemove = useCallback(
    (selectableItem: SelectableItem<T>) => {
      onRemove?.(selectableItem.original);
    },
    [onRemove]
  );

  return {
    selectedItems,
    handleAdd,
    handleRemove,
    searchRef,
    canAddMore
  };
};
